<template>
    <div
        class="l-ss-c-image-container">
        <div
            class="l-ss-c-image-layer"
            :style="imageLayerStyles">
            <cropper
                :resize-image="false"
                :src="scaledBaseImage"
                :default-size="cropperDefaultSize"
                background-class="l-ss-c-image-cropper-background"
                ref="cropper"
                @ready="onCropperReady"
                @change="onCropChange"/>
            <div
                class="l-ss-c-image-tooltip"
                v-if="tooltipStyles"
                :style="tooltipStyles">
                {{ $t('imageSearch.cropHelp') }}
            </div>

            <div
                v-if="$configuration.isShowImageSearchInFilters"
                class="l-ss-c-image-container-replace-button"
                @click="$emit('replace-image')">
                {{ $t('imageSearch.replaceImage') }}
            </div>
        </div>
    </div>
</template>

<script>
    import scaleImage from '@/core/helpers/scale-image';

    const DEFAULT_CANVAS_WIDTH = 300;
    const DEFAULT_CANVAS_HEIGHT = 250;

    export default {
        name: 'section-image',
        components: {
            Cropper: () => import('vue-advanced-cropper').then(({ Cropper }) => Cropper),
        },
        props: {
            baseImage: {
                type: String,
                default: '',
            },
            hasInitialSearch: {
                type: Boolean,
                default: true,
            },
            store: {
                type: Object,
            },
            customCanvasWidth: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                scaledBaseImage: '',
                cropperSize: null,
                resizeCropperObserver: null,
                isInitialSearchEnded: false,
                cropperCoordinates: null,
            };
        },
        computed: {
            canvasWidth() {
                return this.customCanvasWidth || DEFAULT_CANVAS_WIDTH;
            },

            tooltipStyles() {
                const CROPPER_OFFSET = 10;

                if (!this.cropperSize) {
                    return null;
                }

                return {
                    right: this.canvasWidth - this.cropperSize.width + CROPPER_OFFSET + 'px',
                    bottom: DEFAULT_CANVAS_HEIGHT - this.cropperSize.height + CROPPER_OFFSET + 'px',
                };
            },

            imageLayerStyles() {
                return {
                    width: this.canvasWidth,
                    height: DEFAULT_CANVAS_HEIGHT,
                };
            },
        },
        created() {
            this.transformImage();
            this.loadCropperCoordinates();
        },
        methods: {
            async transformImage() {
                this.scaledBaseImage = await scaleImage(this.baseImage, this.canvasWidth, DEFAULT_CANVAS_HEIGHT);
            },
            cropperDefaultSize({ imageSize, visibleArea }) {
                const PERCENT_OFFSET = 3;
                const size = imageSize || visibleArea;

                this.cropperSize = {
                    width: size.width - size.width / 100 * PERCENT_OFFSET,
                    height: size.height - size.height / 100 * PERCENT_OFFSET,
                };

                if (this.cropperCoordinates) {
                    return {
                        ...this.cropperSize,
                        left: this.cropperCoordinates.left,
                        top: this.cropperCoordinates.top,
                    };
                }

                return this.cropperSize;
            },
            onCropperReady() {
                if (this.$refs?.cropper) {
                    this.$nextTick(() => {
                        this.installResizeCropperHandler();

                        if (this.cropperCoordinates) {
                            this.$refs.cropper.setCoordinates(this.cropperCoordinates);
                        }
                    });
                }
            },
            installResizeCropperHandler() {
                const cropperFrame = this.$refs.cropper.$el.querySelector('.vue-rectangle-stencil');

                this.resizeCropperObserver = new MutationObserver(() => {
                    this.cropperSize = null;
                    this.resizeCropperObserver.disconnect();
                    this.resizeCropperObserver = null;
                });

                this.resizeCropperObserver.observe(cropperFrame, {
                    childList: false,
                    subtree: false,
                    attributes: true,
                });
            },
            async onCropChange(params) {
                let image = params.canvas.toDataURL('image/jpeg');
                image = image.replace('data:image/jpeg;base64,', '');

                if (this.hasInitialSearch || this.isInitialSearchEnded) {
                    this.$emit('search', image);
                }

                this.isInitialSearchEnded = true;
                this.saveCropperCoordinates(params.coordinates);
            },
            saveCropperCoordinates(coordinates) {
                this.store.commit('ui/SET_PROPERTY', { key: 'cropperCoordinates', value: coordinates });
            },
            loadCropperCoordinates() {
                this.cropperCoordinates = this.store.state.ui.cropperCoordinates;
            },
        },
    };
</script>
